/**
 * CreditsSheet — modal listing every data source ScoutDex consumes,
 * with the attribution each source requires (or recommends).
 *
 * Opened from the disclaimer footer.  Designed to live alongside the
 * per-card source link so users can see the full provenance picture
 * in one place — game-mechanic data, season news, raid rotations,
 * lore text, the lot.
 *
 * Strict attribution requirements honored here:
 *   - Bulbapedia: name + CC BY-NC-SA 2.5 licence link (per
 *     https://bulbapedia.bulbagarden.net/wiki/Bulbapedia:Copyrights)
 *   - ScrapedDuck: must credit BOTH ScrapedDuck AND LeekDuck.com per
 *     their README; their terms also forbid paywall + ad monetization
 *     (we are non-commercial, free, ad-free — compliant).
 *   - Scopely / pokemongo.com: trademark notice preserved + non-
 *     commercial use only + linkback to source article on each card.
 *     Pokémon GO has been Scopely's since May 2025 (bought from Niantic;
 *     the team was renamed Scopely Explore in July 2026).
 *
 * Non-strict sources (PvPoke MIT, PoGoAPI) are credited as good-faith
 * practice even though no formal requirement exists.
 */
import { useEffect } from "react";

interface CreditsSheetProps {
  open: boolean;
  onClose: () => void;
}

interface SourceEntry {
  name: string;
  url: string;
  used_for: string;
  license: string;
  license_url?: string;
}

const SOURCES: SourceEntry[] = [
  {
    name: "PvPoke",
    url: "https://pvpoke.com/",
    used_for: "GO Battle League rankings, optimal movesets, stat product calculations",
    license: "MIT",
    license_url: "https://github.com/pvpoke/pvpoke/blob/master/LICENSE",
  },
  {
    name: "PoGoAPI",
    url: "https://pogoapi.net/",
    used_for: "Pokémon base stats, type charts, evolution chains, candy costs, move data",
    license: "Public game data (fair use)",
  },
  {
    name: "Bulbapedia",
    url: "https://bulbapedia.bulbagarden.net/",
    used_for: "Pokémon lore, biology, species descriptions",
    license: "CC BY-NC-SA 2.5",
    license_url: "https://creativecommons.org/licenses/by-nc-sa/2.5/",
  },
  {
    name: "LeekDuck",
    url: "https://leekduck.com/",
    used_for: "Raid bosses, Community Days, egg pools, Field Research tasks",
    license: "Fair-use community data (via ScrapedDuck)",
  },
  {
    name: "ScrapedDuck",
    url: "https://github.com/bigfoott/ScrapedDuck",
    used_for: "Machine-readable LeekDuck data feed (events / raids / eggs / research)",
    license: "Open data — credit required for LeekDuck and ScrapedDuck",
  },
  {
    name: "pokemongo.com news",
    url: "https://pokemongo.com/news",
    used_for: "Official GO Battle League season announcements (cup rotation, move balance changes, new moves)",
    license: "© Scopely / The Pokémon Company — non-commercial fan use only",
  },
];

export default function CreditsSheet({ open, onClose }: CreditsSheetProps) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Data sources and credits"
    >
      <div
        className="w-full max-w-2xl bg-scout-elevated rounded-2xl border border-white/[0.08] shadow-2xl mt-4 sm:mt-0 mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-scout-elevated border-b border-white/[0.06] px-5 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Sources & credits</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/50 hover:text-white text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          {/* Trademark notice — Pokémon GO is Scopely's (bought from Niantic in 2025) */}
          <section className="bg-white/[0.02] border border-white/[0.06] rounded-lg px-4 py-3">
            <p className="text-xs text-white/55 leading-relaxed">
              ScoutDex is a non-commercial fan project and is{" "}
              <strong className="text-white/70">not affiliated with, endorsed
              by, or sponsored by</strong> Scopely, The Pokémon Company,
              Nintendo, Game Freak, or Creatures Inc.  Pokémon and its
              trademarks are © 1995–2026 Nintendo / Creatures / Game Freak.
            </p>
          </section>

          {/* Per-source attribution table */}
          <section>
            <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">
              Data sources
            </p>
            <ul className="space-y-3">
              {SOURCES.map((s) => (
                <li key={s.name} className="border border-white/[0.06] rounded-lg px-4 py-3">
                  <div className="flex items-baseline gap-2 mb-1">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-white hover:text-scout-blue transition-colors underline-offset-2 hover:underline"
                    >
                      {s.name}
                    </a>
                    <span className="text-[10px] text-white/40">
                      {s.license_url ? (
                        <a
                          href={s.license_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-white/70 transition-colors underline-offset-2 hover:underline"
                        >
                          {s.license}
                        </a>
                      ) : (
                        s.license
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-white/60 leading-snug">{s.used_for}</p>
                </li>
              ))}
            </ul>
          </section>

          <p className="text-[11px] text-white/35 leading-relaxed">
            Every info card in the app links back to its specific source
            in the footer.  This sheet collects the full list so you can
            see the provenance picture at a glance.
          </p>
        </div>
      </div>
    </div>
  );
}
